import {
    AreaNotification,
    Breakpoint,
    DisplayField,
    domain,
    JobType,
    Layout,
    LayoutContainer,
    TagType,
    Text,
    useNotifications,
} from "@relode/core";
import { format } from "date-fns";
import * as React from "react";

import { useClientPublicJobContext } from "../_infra/clientPublicJobProvider";

const jobPositionString = (position: domain.JobPosition) =>
    position.jobPositionName;

const tagList = (tags: TagType[]) => {
    if (!tags || tags.length === 0) return null;
    return tags.map(t => t.tagName).join(" • ");
};

export const ClientPublicJobOverview = () => {
    const {
        state: {
            data: {
                job: { details },
            },
        },
    } = useClientPublicJobContext();
    const { actions: notifications } = useNotifications();

    React.useEffect(() => {
        if (details.jobStatusMessage) {
            const message = !!details.statusReasonTimestamp
                ? `${details.jobStatusMessage} ${format(
                      details.statusReasonTimestamp,
                      "P p"
                  )}`
                : details.jobStatusMessage;
            notifications.showArea("info", message, "jobStatusMessage", {
                canClose: false,
            });
        } else {
            notifications.closeAreaNotification("jobStatusMessage");
        }
    }, [details, notifications]);

    const contractDateRange = (start, end) => {
        if (!start) return null;
        return [start, end].join(" - ");
    };

    const jobPositions = [
        ...details.secondaryJobPositions,
        details.primaryJobPosition,
    ]
        .filter(item => !!item)
        .map(jobPositionString)
        .join(" • ");

    const benefitsList = tagList(details.benefits);

    return (
        <LayoutContainer>
            <AreaNotification areaName="jobStatusMessage" />
            <Layout mode="50/50">
                <Layout mode="100">
                    <Breakpoint mobile>
                        <DisplayField
                            label="organization"
                            value={details.teamName}
                        />
                    </Breakpoint>
                    <DisplayField
                        label="job type"
                        value={details.jobType.jobTypeName}
                    />
                    <DisplayField
                        label="job positions"
                        value={jobPositions}
                        hideWhenEmpty
                    />

                    <DisplayField
                        label="location"
                        component={
                            <>
                                <Text dataCy="locationName" variant="body1">
                                    {details.locationName}
                                </Text>
                            </>
                        }
                    />
                    <DisplayField
                        label="open positions"
                        value={details.openPositions}
                    />
                    {JobType.isContractOrLocum(details.jobType.jobTypeId) ? (
                        <>
                            <DisplayField
                                label="contract date range"
                                value={contractDateRange(
                                    details.contractStartDate,
                                    details.contractEndDate
                                )}
                            />
                            <DisplayField
                                label="hours/week"
                                value={details.hoursPerWeek}
                            />
                        </>
                    ) : null}
                    {JobType.isFullTimeOrPartTime(details.jobType.jobTypeId) ? (
                        <>
                            <DisplayField
                                label="hours/week"
                                value={details.hoursPerWeek}
                            />
                        </>
                    ) : null}
                    <DisplayField
                        label="benefits"
                        value={benefitsList}
                        hideWhenEmpty
                    />
                </Layout>
                <Layout mode="100">
                    <DisplayField
                        label="overview"
                        value={details.overview}
                        preserveTextFormat
                    />
                </Layout>
            </Layout>
        </LayoutContainer>
    );
};
